import classNames from "classnames";
import "custom-event-polyfill";
import React, { createRef } from "react";
import { Route, StaticContext, Switch } from "react-router";
import { RouteComponentProps } from "react-router-dom";
import LoadingBar from "Shared/LoadingBar";
import { analyticsFEA } from "src/AnalyticsFEA";
import { AccessoriesDrawer } from "src/components/Accessories";
import { ConditionalRebatesDrawer } from "src/components/ConditionalRebates";
import BankOffersDrawer from "src/components/MyGarage/BankOffers/BankOffersDrawer";
import { ApiVehicle } from "Types/api/Vehicle";
import { DealState } from "Types/state";
import GlobalPaymentsControl from "./GlobalPaymentsControl";
import "./MyGarage.scss";
import TableCompare from "./TableCompare";
import TradePendingDrawer from "./TradePendingDrawer";
import * as smoothScroll from "smoothscroll-polyfill";
import TestDrive from "src/components/TestDrive/TestDriveModal";
import MessageDrawer from "src/components/Messaging/MessageDrawer/MessageDrawer";
import withFrame, { WithFrameProps } from "src/providers/frame/withFrame";
import BannerNotification from "src/components/MyGarage/BannerNotification";

export interface MyGarageProps extends RouteComponentProps<{}, StaticContext, { vin: string }> {
    deal: DealState;
    isViewingSoftCreditRatingForm: boolean;
    open: boolean;
    region: string;
    vehicles: ApiVehicle[];
}

export class MyGarage extends React.Component<MyGarageProps & WithFrameProps, { viewed: boolean }> {
    public rootRef = createRef<HTMLDivElement>();
    private dropHistoryListener: () => void;

    public constructor(props: MyGarageProps & WithFrameProps) {
        super(props);
        this.state = {
            viewed: false,
        };
        /* First checks if smooth scrolling is natively supported by the browser,
           if not it allows us to opt in to native smooth scrolling.            */
        smoothScroll.polyfill();
    }

    public componentDidMount() {
        this.dropHistoryListener = this.props.history.listen((location, action) => {
            if (location.pathname === "/my-garage" && location.state && location.state?.vin) {
                this.scrollToPrevPositionAfterDrawerClose();
            } else this.handleScroll();
        });
    }

    public componentDidUpdate(prevProps: any) {
        if (analyticsFEA.isInitialized() && !this.state.viewed) {
            this.setState({ viewed: true });
        }
        /* This event prevents the sliders from being
            mis-sized and not functioning properly. */
        window.dispatchEvent(new CustomEvent("resize"));

        if (
            this.props.isViewingSoftCreditRatingForm &&
            prevProps.isViewingSoftCreditRatingForm !== this.props.isViewingSoftCreditRatingForm
        ) {
            this.handleScroll();
        }
    }

    public componentWillUnmount() {
        this.dropHistoryListener();
    }

    public render() {
        const { deal, open } = this.props;
        if (!(deal && deal.person)) {
            return (
                <div className="LoadingBar__container">
                    <LoadingBar />
                </div>
            );
        }
        return (
            <div className={classNames("MyGarage", { open })} ref={this.rootRef}>
                <div className={classNames("MyGarage__gpcContainer", { open })}>
                    <Switch>
                        <Route path="/my-garage/accessories" component={AccessoriesDrawer} />
                        <Route path="/my-garage/conditional-rebates" component={ConditionalRebatesDrawer} />
                        <Route path="/my-garage/payment-settings" component={GlobalPaymentsControl} />
                        <Route path="/my-garage/trade-in" component={TradePendingDrawer} />
                        <Route path="/my-garage/bank-offers" component={BankOffersDrawer} />
                        <Route path="/my-garage/test-drive" component={TestDrive} />
                        <Route path="/my-garage/message-center" component={MessageDrawer} />
                        <Route>{null}</Route>
                    </Switch>
                </div>
                <div className={classNames("MyGarage__container", { open })}>
                    <BannerNotification />
                    <TableCompare />
                </div>
            </div>
        );
    }

    private handleScroll = () => {
        setTimeout(async () => {
            if (this.rootRef && this.rootRef.current) {
                this.rootRef.current.scrollIntoView({ behavior: "smooth" });
            }
        }, 300);
    };

    /* Handles scroll to previous vehicle column after drawer close. */
    private scrollToPrevPositionAfterDrawerClose = () => {
        setTimeout(() => {
            this.props.iframe.document
                .querySelector(`[data-vin="${(this.props.location.state as { vin: string })?.vin}"]`)
                ?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        }, 800);
    };
}

export default withFrame(MyGarage);
